import { useState, useEffect, useRef } from 'react';
import { Upload, CheckCircle, Clock, XCircle, Star, ImageIcon, AlertCircle } from 'lucide-react';
import { createApiClient } from '../services/api';
import { useAppAuth } from '../hooks/useAppAuth';

type Platform = 'Checkatrade' | 'Trustpilot' | 'Google' | 'Other';
type Role = 'Carrier' | 'Workshop';

interface ScreenshotUpload {
  uploadId: string;
  platform: string;
  userRole: string;
  originalFileName: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  approvedRating: number | null;
  approvedReferenceText: string | null;
  rejectionReason: string | null;
  uploadedAt: string;
  reviewedAt: string | null;
}

const PLATFORMS: Platform[] = ['Checkatrade', 'Trustpilot', 'Google', 'Other'];

const PLATFORM_HINTS: Record<Platform, string> = {
  Checkatrade: 'Screenshot your Checkatrade profile page showing your star rating and review count.',
  Trustpilot:  'Screenshot your Trustpilot business page showing your TrustScore and number of reviews.',
  Google:      'Screenshot your Google Business profile showing your star rating and review count.',
  Other:       'Screenshot any verifiable external ratings page that clearly shows your business name and rating.',
};

function StatusBadge({ status }: { status: string }) {
  if (status === 'Approved') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <CheckCircle className="w-3 h-3" />Approved
    </span>
  );
  if (status === 'Rejected') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
      <XCircle className="w-3 h-3" />Rejected
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
      <Clock className="w-3 h-3" />Pending Review
    </span>
  );
}

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
        />
      ))}
      <span className="ml-1 text-sm font-semibold text-gray-700">{(rating ?? 0).toFixed(1)}</span>
    </span>
  );
}

export default function ExternalRatingUploadPage() {
  const { getToken } = useAppAuth();
  const [uploads, setUploads]     = useState<ScreenshotUpload[]>([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [success, setSuccess]     = useState<string | null>(null);

  const [platform, setPlatform] = useState<Platform>('Checkatrade');
  const [userRole, setUserRole] = useState<Role>('Carrier');
  const [file, setFile]         = useState<File | null>(null);
  const fileInputRef            = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    createApiClient(getToken).get<ScreenshotUpload[]>('/api/v1/ratings/screenshots/my')
      .then(r => setUploads(r.data))
      .catch(() => setError('Failed to load your uploads.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a screenshot to upload.'); return; }

    setError(null);
    setSuccess(null);
    setUploading(true);

    const form = new FormData();
    form.append('screenshot', file);
    form.append('platform', platform);
    form.append('userRole', userRole);

    try {
      await createApiClient(getToken).post('/api/v1/ratings/screenshots', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('Screenshot uploaded successfully. Our support team will review it shortly.');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (err: any) {
      setError(err?.response?.data?.title ?? err?.response?.data ?? 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="page-shell space-y-8">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">External Rating Verification</h1>
        <p className="mt-1 text-sm text-gray-500">
          Upload a screenshot of your CheckaTrade, Trustpilot, or Google rating.
          Our support team will verify it and display your approved rating on your public profile.
        </p>
      </div>

      {/* Upload form */}
      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2">
          <Upload className="w-4 h-4 text-blue-500" />
          Upload New Screenshot
        </h2>

        {/* Role selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Role</label>
          <div className="flex gap-3">
            {(['Carrier', 'Workshop'] as Role[]).map(r => (
              <button
                key={r}
                type="button"
                onClick={() => setUserRole(r)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  userRole === r
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Platform selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Rating Platform</label>
          <div className="grid grid-cols-2 gap-2">
            {PLATFORMS.map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPlatform(p)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium border text-left transition-colors ${
                  platform === p
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">{PLATFORM_HINTS[platform]}</p>
        </div>

        {/* File input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Screenshot (PNG or JPEG, max 5 MB)
          </label>
          <div
            className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center
                       hover:border-blue-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            {file ? (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                <ImageIcon className="w-5 h-5 text-blue-500" />
                <span className="font-medium">{file.name}</span>
                <span className="text-gray-400">({(file.size / 1024).toFixed(0)} KB)</span>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  Click to choose a screenshot, or drag and drop
                </p>
                <p className="text-xs text-gray-400 mt-1">PNG, JPEG, WebP up to 5 MB</p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {error   && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}
        {success && (
          <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {success}
          </div>
        )}

        <button
          type="submit"
          disabled={uploading || !file}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700
                     disabled:bg-blue-400 text-white font-medium py-2.5 px-4 rounded-lg
                     transition-colors"
        >
          {uploading
            ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />Uploading</>
            : <><Upload className="w-4 h-4" />Submit for Verification</>
          }
        </button>
      </form>

      {/* Upload history */}
      <div>
        <h2 className="font-semibold text-gray-800 mb-3">Your Submissions</h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : uploads.length === 0 ? (
          <div className="card p-8 text-center text-gray-400">
            <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No screenshots submitted yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {uploads.map(u => (
              <div key={u.uploadId} className="card p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <span className="font-medium text-gray-800">{u.platform}</span>
                    <span className="ml-2 text-xs text-gray-400">{u.userRole}</span>
                  </div>
                  <StatusBadge status={u.status} />
                </div>

                <p className="text-xs text-gray-400 truncate">{u.originalFileName}</p>
                <p className="text-xs text-gray-400">
                  Uploaded {new Date(u.uploadedAt).toLocaleDateString()}
                  {u.reviewedAt && ` · Reviewed ${new Date(u.reviewedAt).toLocaleDateString()}`}
                </p>

                {u.status === 'Approved' && u.approvedRating != null && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <StarRating rating={u.approvedRating} />
                    {u.approvedReferenceText && (
                      <p className="mt-1 text-xs text-gray-600 italic">"{u.approvedReferenceText}"</p>
                    )}
                  </div>
                )}

                {u.status === 'Rejected' && u.rejectionReason && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <p className="text-xs text-red-600">
                      <span className="font-medium">Reason: </span>{u.rejectionReason}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">Please re-upload a clearer screenshot.</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

